import { t } from "i18next";
import i18n from "../i18n";
import { IDays } from "../interface/GeneralResponse";
import moment from "moment";

export const capitalizeEachWord = (s: string): string => {
  if (typeof s !== "string" || s.length === 0) return "";
  return s
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const formatDatePicker = (timestamp: string | null): string | null => {
  if (timestamp != null) {
    timestamp = timestamp.replace(/[^\d-]/g, "");
  }
  if (!timestamp) return null;
  const date = new Date(Number(timestamp) * 1000);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

export const formatDateTimeToShow = (
  timestampString: string,
  language: string = i18n.language,
) => {
  const timestamp = parseFloat(timestampString);

  if (isNaN(timestamp)) {
    throw new Error("Invalid timestamp");
  }

  const date = new Date(timestamp * 1000);

  return new Intl.DateTimeFormat(language, {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);
};

export const findItem = <T, K extends keyof T>(
  arr: T[],
  key: K,
  target: T[K],
): T[] | undefined => {
  return arr?.filter((e) => e[key] === target);
};

export const flattenObject = (
  obj: Record<string, any>,
  parentKey: string = "",
  result: Record<string, any> = {},
): Record<string, any> => {
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const newKey = parentKey ? `${parentKey}.${key}` : key;
      if (
        obj[key] &&
        typeof obj[key] === "object" &&
        !(obj[key] instanceof File)
      ) {
        flattenObject(obj[key], newKey, result);
      } else {
        result[newKey] = obj[key];
      }
    }
  }
  return result;
};

export function formatAge(
  birthdayDate: string,
  notTimestamp?: boolean,
): number {
  if (birthdayDate != null) {
    birthdayDate = birthdayDate.replace(/[^\d-]/g, ""); // Sanitize input
  }

  if (notTimestamp) {
    // Handle date strings like "10-09-2024" (MM-DD-YYYY format)
    const birthday = moment(birthdayDate, "DD-MM-YYYY");

    const age = moment().diff(birthday, "years");

    return age === 0 ? 1 : age; // Ensure age is at least 1
  } else { 
    const birthday = moment.unix(parseInt(birthdayDate));

    const age = moment().diff(birthday, "years");
    return age === 0 ? 1 : age;
  }
}

export function getGenderEmoji(gender: string): string {
  const genderTrimmed = gender?.trim()?.toLowerCase();
  if (genderTrimmed === "male" || genderTrimmed === "ذكر") {
    return " ♂ ";
  } else if (genderTrimmed === "female" || genderTrimmed === "أنثى") {
    return " ♀ ";
  } else {
    return "";
  }
}

export const zoomImage = (event: React.MouseEvent<HTMLImageElement>) => {
  event.currentTarget.requestFullscreen();
};

export const isOlderThan15 = (dateString: string): boolean => {
  const [day, month, year] = dateString.split("-").map(Number);
  const birthDate = new Date(year, month - 1, day);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();

  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age >= 15;
};

export const mapDayIdToName = (dayId: number, days: IDays[]): string => {
  const day = days.find((d) => d.Id === dayId);
  return day ? day.Name : "Unknown Day";
}; 
 
export const normalizeGender = (gender: string | null | undefined): string => {
  if (!gender) return "";

  const trimmedGender = gender.trim().toLowerCase();

  switch (trimmedGender) {
    case "male":
      return t("male");
    case "female":
      return t("female");
    default:
      return "";
  }
};

export function formatDoctorName(
  fullName: string | null | undefined,
  fullNameAr?: string | null | undefined,
  fullNameEn?: string | null | undefined,
): string {
  if (fullNameAr && fullNameEn) {
    return `${t("dr")} ${fullName}`;
  } else if (!fullNameAr && fullNameEn) {
    return `Dr. ${fullNameEn}`;
  } else if (fullNameAr && !fullNameEn) {
    return `د. ${fullNameAr}`;
  } else {
    return fullName || "";
  }
}

export function formatNumberWithCommas(number: number): string {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export const copyToClipboard = async (text: string, showToast: any) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
    } else {
      // Fallback for HTTP or unsupported browsers
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }
    showToast(t("copied_to_clipboard"), "success");
  } catch (error) {
    showToast("Failed to copy!", "error");
  }
}; 

export function calculateAge(dateString: string) {
  const [day, month, year] = dateString?.split("-").map(Number);
  const birthDate = new Date(year, month - 1, day);
  const currentDate = new Date();
  let age = currentDate.getFullYear() - birthDate.getFullYear();
  const monthDiff = currentDate.getMonth() - birthDate.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && currentDate.getDate() < birthDate.getDate())
  ) {
    age--;
  }
  return Math.max(age, 1);
}

export const currentLanguage = (language: string) => language?.slice(0, 2);
export const isArabic = (language: string) => language?.slice(0, 2) === "ar";
export const isEnglish = (language: string) => language?.slice(0, 2) === "en";

export const formatBirthday = (
  birthday: string | undefined | null,
  lang: string = "ar",
): string => {
  if (!birthday) return "-";

  let date: Date | null = null;

  const cleanValue = String(birthday).trim();

  if (cleanValue.includes("-") && cleanValue.split("-").length === 3) {
    const parts = cleanValue.split("-");
    if (parts.every((p) => !isNaN(Number(p)))) {
      let day: number, month: number, year: number;

      if (Number(parts[0]) > 31) {
        year = Number(parts[0]);
        month = Number(parts[1]) - 1;
        day = Number(parts[2]);
      } else {
        day = Number(parts[0]);
        month = Number(parts[1]) - 1;
        year = Number(parts[2]);
      }

      date = new Date(year, month, day);
    }
  }

  if (!date && cleanValue.includes("/")) {
    const parts = cleanValue.split("/");
    if (parts.every((p) => !isNaN(Number(p))) && parts.length === 3) {
      let day: number, month: number, year: number;

      day = Number(parts[0]);
      month = Number(parts[1]) - 1;
      year = Number(parts[2]);

      date = new Date(year, month, day);
    }
  }

  if (!date) {
    const numericValue = Number(cleanValue.replace(/[^0-9-]/g, ""));
    if (!isNaN(numericValue) && numericValue !== 0) {
      if (numericValue > 9999999999) {
        date = new Date(numericValue);
      } else {
        date = new Date(numericValue * 1000);
      }
    }
  }

  if (!date || isNaN(date.getTime())) {
    const parsed = new Date(cleanValue);
    if (!isNaN(parsed.getTime())) {
      date = parsed;
    }
  }

  if (!date || isNaN(date.getTime())) {
    return "-";
  }

  const day = date.getDate();
  const year = date.getFullYear();
  const monthIndex = date.getMonth();

  const isArabic = lang.toLowerCase().startsWith("ar");

  const monthNames = isArabic
    ? [
        "يناير",
        "فبراير",
        "مارس",
        "أبريل",
        "مايو",
        "يونيو",
        "يوليو",
        "أغسطس",
        "سبتمبر",
        "أكتوبر",
        "نوفمبر",
        "ديسمبر",
      ]
    : [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];

  return `${day} ${monthNames[monthIndex]}, ${year}`;
};

export function formatTime24To12(time24: string | Date): string {
  if (!time24) return "";
 
  if (time24 instanceof Date) {
    const hours = time24.getHours();
    const minutes = time24.getMinutes();
    const period = hours >= 12 ? t("pm") : t("am");
    const hour12 = hours % 12 || 12;
    return `${String(hour12).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${period}`;
  }
 
  if (typeof time24 === "string") {
    const [hourStr, minuteStr] = time24.split(":");
    if (!hourStr || !minuteStr) return "";

    let hour = parseInt(hourStr, 10);
    const minute = parseInt(minuteStr, 10);

    if (isNaN(hour) || isNaN(minute)) return "";

    const period = hour >= 12 ? t("pm") : t("am");
    const hour12 = hour % 12 || 12;

    return `${String(hour12).padStart(2, "0")}:${String(minute).padStart(2, "0")} ${period}`;
  }

  return "";
}

